import { useEffect, useMemo, useRef, useState } from 'react';
import { Typography, Flex, Table } from 'antd';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { theme } from 'antd';
import {
  poReceiveApproveByITLeader,
  searchPoReceivingDetailsByPoNumberAndDate,
} from 'src/redux/features/po/ReceiveHours/Action';
import {
  fetchAllPos,
  searchPoDetailsByPoNumber,
} from 'src/redux/features/po/ResourceMapping/Action';
import { getAllPOs } from 'src/redux/features/po/ResourceMapping/Slice';
const { Title } = Typography;

import { IPurchaseOrder } from '../../models/PurchaseOrder';
import dayjs from 'dayjs';
import { IPurchaseOrderLine } from 'src/models/PoReceiving';
import SharedDatePicker from 'src/components/DatePicker';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import ResourceWorkingHoursModal from 'src/components/ResourceWorkingHourModal';
import AntDButton from 'src/components/Button';
import PODetailTable from 'src/components/PODetailTable';
import CustomSplitter from 'src/components/Splitter';
import { IAllPurchaseOrderResp } from 'src/models/AllPurchaseOrder';
import { ScrollableContent } from 'src/components/ScrollableContent';
import en from 'src/messages/en.json';
import { setError } from 'src/redux/features/common/commonSlice';

const PoReceiving = () => {
  const { token } = theme.useToken();
  const dispatch = useDispatch();
  const [data, setData] = useState<IPurchaseOrder[]>([]);
  const [poData, setPoData] = useState<IPurchaseOrderLine[]>([]);
  const [purchaseData, setPurchaseData] = useState<IAllPurchaseOrderResp[]>([]);
  const [filteredData, setFilteredData] = useState<IAllPurchaseOrderResp[]>([]);

  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedPo, setSelectedPo] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [searchValueByVendor, setSearchValueByVendor] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [totalHours, setTotalHours] = useState(0);
  const [descriptionEndDate, setDescriptionEndDate] = useState<Date | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [expandedItems, setExpandedItems] = useState<number[]>(
    poData?.map((item) => item?.poLineId),
  );
  const [isSecondPanelVisible, setIsSecondPanelVisible] =
    useState(!!selectedPo);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPOTableLoading, setIsPOTableLoading] = useState(false);
  const [modalData, setModalData] = useState<any[]>([]);
  const [totalDaysData, setTotalDaysData] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [totalCount, setTotalCount] = useState(0);
  const activeRequest = useRef<any>(null);

  const handleOpenModal = (resource: any, userTotalDays: number) => {
    setModalData(resource);
    setTotalDaysData(userTotalDays);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setTotalDaysData(0);
    setModalData(null);
  };

  //expan & collapse
  const toggleItem = (id: number) => {
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const fetchProjects = async () => {
    if (!selectedPo) return;

    try {
      const response = await dispatch(
        searchPoDetailsByPoNumber({ purchaseOrder: selectedPo }),
      );

      return setData(response.payload ? [response.payload] : []);
    } catch (error) {
      return dispatch(setError(en.POs.Error.fetchPOInfo));
    }
  };
  const fetchPoReceiving = async () => {
    if (!selectedPo || !descriptionEndDate) return;
    const payload = {
      purchaseOrder: selectedPo,
      endDate: descriptionEndDate
        ? dayjs(descriptionEndDate).format('MM-DD-YYYY')
        : '',
    };

    setIsLoading(true);

    try {
      const response = await dispatch(
        searchPoReceivingDetailsByPoNumberAndDate(payload),
      );

      return setPoData(response.payload);
    } catch (error) {
      return dispatch(setError(en.POs.Error.fetchPOReceivingInfo));
    } finally {
      setIsLoading(false);
    }
  };
  const onSave = async () => {
    setIsSaving(true);
    const payload = {
      purchaseOrderNumber: selectedPo,
      startDate: data[0].startDate
        ? dayjs(data[0].startDate).format('YYYY-MM-DD')
        : '',
      endDate: descriptionEndDate
        ? dayjs(descriptionEndDate).format('YYYY-MM-DD')
        : '',
      hours: totalHours,
    };

    try {
      const response = await dispatch(poReceiveApproveByITLeader(payload));

      setPoData(response.payload);
      setIsSaving(false);
      setDescriptionEndDate(null);
      setTotalHours(0);
      fetchProjects();

      return toast.success(en.POs.Success.receiveRequestGenerated);
    } catch (error) {
      return dispatch(setError(en.POs.Error.generateReceiveRequest));
    }
  };

  const handleSelectionVendorChange = (selected: string | null) => {
    setSelectedVendor(selected ?? null);
  };

  const handleSelectionPoNumberChange = (selected: number | null) => {
    setSelectedPo(selected?.toString() ?? undefined);
  };
  const handleRangeDateChange = (
    startDate: Date | null,
    endDate: Date | null,
  ) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };
  const handleDescriptionEndDateChange = (date: Date | null) => {
    setDescriptionEndDate(date);
  };

  const disableDates = (date: Date) => {
    const normalizedDate = new Date(date);

    normalizedDate.setHours(0, 0, 0, 0);

    return data[0]?.receivedDates?.some(({ startDate, endDate }) => {
      const start = new Date(startDate);

      start.setHours(0, 0, 0, 0);

      const end = new Date(endDate);

      end.setHours(23, 59, 59, 999);

      return normalizedDate >= start && normalizedDate <= end;
    });
  };
  const isDateRangeInvalid = (startDate: Date, endDate: Date): boolean => {
    const currentDate = new Date(startDate);

    currentDate.setHours(0, 0, 0, 0);

    const end = new Date(endDate);

    end.setHours(0, 0, 0, 0);

    while (currentDate <= end) {
      if (disableDates(currentDate)) {
        console.log(
          `Invalid date found: ${currentDate.toISOString().split('T')[0]}`,
        );

        return true;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return false;
  };
  const fetchData = async ({
    poNumber = '',
    vendorName = '',
    startDate = null,
    endDate = null,
    pageNumber = 1,
    pageSize = 20,
    sortField = '',
    sortDirection = 'asc',
  }) => {
    setIsPOTableLoading(true);
    if (activeRequest.current) {
      activeRequest.current.abort();
    }
    let promise: any;

    try {
      const rawPayload = {
        PoNumber: poNumber || undefined,
        VendorName: vendorName || undefined,
        StartDate: startDate
          ? dayjs(startDate).format('YYYY-MM-DD')
          : undefined,
        EndDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
        PageNumber: pageNumber,
        PageSize: pageSize,
        SortField: sortField || undefined,
        SortDirection: sortDirection,
      };

      const payload: Record<string, any> = {};

      Object.entries(rawPayload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          payload[key] = value;
        }
      });
      promise = dispatch(fetchAllPos(payload));

      activeRequest.current = promise; // store latest promise

      const data = await promise.unwrap();

      if (
        data.totalCount > 0 &&
        payload.pageNumber > Math.ceil(data.totalCount / payload.pageSize)
      ) {
        // If user is on invalid page, go back to last valid page
        setPageNumber(1);
      } else {
        setPurchaseData(data.data);
        setTotalCount(data.totalCount);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log('Previous request canceled');
      } else {
        console.error('Fetch failed:', err);
      }
    } finally {
      if (activeRequest.current === promise) {
        setIsPOTableLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [dispatch, selectedPo]);

  useEffect(() => {
    fetchPoReceiving();
  }, [dispatch, selectedPo, descriptionEndDate]);
  useEffect(() => {
    if (!descriptionEndDate) {
      return setTotalHours(0);
    }
    const receiveTotalHours = poData.reduce((total, item) => {
      return (
        total +
        item.resources.reduce((rowTotal, res) => rowTotal + res.hours, 0)
      );
    }, 0);

    setTotalHours(receiveTotalHours);
  }, [poData, descriptionEndDate]);
  const columnsAntd = [
    {
      title: en.POs.Title.line,
      dataIndex: 'line',
      key: 'line',
      width: '10%',
      ellipsis: true,
      render: (_: any, record: IPurchaseOrderLine) => `${record.lineNum}`,
    },
    {
      title: en.POs.Title.itemDescription,
      dataIndex: 'itemDescription',
      key: 'itemDescription',
      ellipsis: true,
      width: '40%',
      render: (_: any, record: IPurchaseOrderLine) => (
        <div
          style={{
            maxWidth: 300,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {record.itemDescription}
        </div>
      ),
    },
    {
      title: '',
      key: 'toggle',
      width: '10%',
      render: (_: any, record: IPurchaseOrderLine) => {
        if (!record.resources || record.resources.length === 0) {
          return null;
        }

        return (
          <span
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer', width: '40px' }}
            onClick={() => toggleItem(record.poLineId)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                toggleItem(record.poLineId);
              }
            }}
          >
            {expandedItems.includes(record.poLineId) ? (
              <UpOutlined />
            ) : (
              <DownOutlined />
            )}
          </span>
        );
      },
    },
    {
      title: en.POs.Title.approvedHours,
      dataIndex: 'approvedHours',
      key: 'approvedHours',
      ellipsis: true,
      render: (_: any, record: IPurchaseOrderLine) => record.lineTotalHours,
    },
  ];
  const expandedRowRender = (record: IPurchaseOrderLine) => {
    return (
      <div>
        {record.resources.map((resource, idx) => {
          const dateRange = resource.dateRanges;

          const userTotalDays = resource.dateRanges.reduce(
            (innerSum: number, range: any) => {
              const start = dayjs(range?.startDate);
              const end = dayjs(range?.endDate);
              const days = end.diff(start, 'day') + 1;

              return innerSum + days;
            },
            0,
          );

          return (
            <div
              key={idx}
              style={{
                marginBottom: '0.25rem',
                marginLeft: '15%',
              }}
            >
              {resource.name}&nbsp;
              <span
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (dateRange?.length > 0) {
                    handleOpenModal(resource, userTotalDays);
                  }
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLSpanElement>) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleOpenModal(resource, userTotalDays);
                  }
                }}
                style={{
                  cursor: userTotalDays === 0 ? 'default' : 'pointer',
                  fontWeight: 300,
                  textDecoration: userTotalDays === 0 ? 'none' : 'underline',
                  color: userTotalDays === 0 ? 'black' : token.colorPrimary,
                }}
              >
                {userTotalDays === 0
                  ? ''
                  : userTotalDays === 1
                    ? '1 day'
                    : `${userTotalDays} days`}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const LeftPanelContent = () => (
    <PODetailTable
      data={purchaseData}
      setSelectedPO={setSelectedPo}
      searchValue={searchValue}
      searchValueByVendor={searchValueByVendor}
      startDate={startDate}
      endDate={endDate}
      setSearchValue={setSearchValue}
      setSearchValueByVendor={setSearchValueByVendor}
      handleRangeDateChange={handleRangeDateChange}
      isPOTableLoading={isPOTableLoading}
      setIsSecondPanelVisible={setIsSecondPanelVisible}
      selectedPO={selectedPo}
      pageNumber={pageNumber}
      setPageNumber={setPageNumber}
      pageSize={pageSize}
      setPageSize={setPageSize}
      totalCount={totalCount}
      sortField={sortField}
      setSortField={setSortField}
      sortDirection={sortDirection}
      setSortDirection={setSortDirection}
      setTotalCount={setTotalCount}
    />
  );

  const RightPanelContent = () => (
    <div style={{ padding: '0.25rem 0 0 1rem' }}>
      {/* PO Details Heading */}
      <Title level={5} style={{ textAlign: 'left' }}>
        {en.POs.Title.poDetails}
      </Title>

      {/* PO Number & Description */}
      <Flex
        align="center"
        gap={'middle'}
        wrap="nowrap"
        style={{ padding: '0 0 0.75rem' }}
      >
        <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>
          {en.POs.Label.poNumber}{' '}
          <span style={{ fontWeight: 'normal' }}>{selectedPo} </span>
        </Typography.Text>

        <Flex style={{ flex: 1, overflow: 'hidden' }} align="start">
          <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>
            {en.POs.Label.poDescription}&nbsp;
          </Typography.Text>
          <Typography.Text
            style={{
              fontWeight: 'normal',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              flex: 1,
              textAlign: 'left',
            }}
            title={data[0]?.poDescription}
            ellipsis
          >
            {data[0]?.poDescription}
          </Typography.Text>
        </Flex>
      </Flex>
      <Flex
        align="center"
        gap={'large'}
        wrap="nowrap"
        justify="space-between"
        style={{ padding: '0 0 0.75rem' }}
      >
        <Flex gap={'small'} align="center">
          <Typography.Text
            strong
            style={{
              whiteSpace: 'nowrap',
            }}
          >
            {en.POs.Label.receivedDate}{' '}
          </Typography.Text>
          <SharedDatePicker
            style={{
              width: '100%',
            }}
            isRange={false}
            placeholder={en.Common.Placeholder.selectDate}
            date={descriptionEndDate}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' || e.key === 'Delete') {
                e.preventDefault();
                handleDescriptionEndDateChange(null);
              }
            }}
            onChange={handleDescriptionEndDateChange}
            disableFutureDates={true}
            min={new Date(data[0]?.startDate)}
            dateFormat="MM/DD/YYYY"
          />
        </Flex>

        <Typography.Text style={{ whiteSpace: 'nowrap' }}>
          {en.POs.Label.totalHours}
          {totalHours}
        </Typography.Text>
      </Flex>
      <ScrollableContent
        minHeight={'calc(100vh - 18.75rem)'}
        maxHeight={'calc(100vh - 18.75rem)'}
      >
        <Table
          dataSource={descriptionEndDate && selectedPo && poData}
          columns={columnsAntd}
          rowKey="poLineId"
          pagination={false}
          expandable={{
            expandedRowKeys: expandedItems,
            onExpand: (expanded, record) => toggleItem(record.poLineId),
            expandedRowRender,
            expandIcon: () => null,
            expandIconColumnIndex: -1,
          }}
          sticky
          loading={isLoading}
          bordered={false}
          size="small"
        />
      </ScrollableContent>
      <ResourceWorkingHoursModal
        open={isModalVisible}
        onClose={handleCloseModal}
        data={modalData}
        isManagePO={false}
        totalDays={totalDaysData}
      />
      <div
        style={{
          marginTop: '0.5rem',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <AntDButton
          type="primary"
          onClick={onSave}
          disabled={!totalHours}
          status="Receiving..."
          statusOnComplete="Receive Hours"
          loading={isSaving}
          htmlType="button"
        ></AntDButton>
      </div>
    </div>
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      if (
        (searchValue.length === 0 || searchValue.length >= 3) &&
        (searchValueByVendor.length === 0 || searchValueByVendor.length >= 3)
      ) {
        fetchData({
          poNumber: searchValue,
          vendorName: searchValueByVendor,
          startDate,
          endDate,
          pageNumber,
          pageSize,
          sortField,
          sortDirection,
        });
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [
    searchValue,
    searchValueByVendor,
    startDate,
    endDate,
    pageNumber,
    pageSize,
    sortField,
    sortDirection,
  ]);

  return (
    <>
      <Title level={4} style={{ textAlign: 'left' }}>
        {en.POs.Title.receiveRequests}
      </Title>
      <CustomSplitter
        firstPanelContent={LeftPanelContent}
        secondPanelContent={RightPanelContent}
        isSecondPanelVisible={isSecondPanelVisible}
        setIsSecondPanelVisible={setIsSecondPanelVisible}
      />
    </>
  );
};

export default PoReceiving;
